import { cva, type VariantProps } from 'class-variance-authority';

import type { ComponentProps, JSX } from 'react';

import { cn } from '~/utils';

const cardVariants = cva(
  'bg-card text-card-foreground flex flex-col rounded-xl border shadow-sm',
  {
    variants: {
      size: {
        md: 'py-6 gap-6',
        sm: 'py-4 gap-2',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export function Card({
  size,
  className,
  ...props
}: ComponentProps<'div'> & VariantProps<typeof cardVariants>): JSX.Element {
  return (
    <div
      data-slot='card'
      className={cn(
        cardVariants({
          size,
        }),
        className,
      )}
      {...props}
    />
  );
}

const cardHeaderVariants = cva(
  '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 has-data-[slot=card-action]:grid-cols-[1fr_auto]',
  {
    variants: {
      size: {
        md: 'px-6 [.border-b]:pb-6',
        sm: 'px-4 [.border-b]:pb-4',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export function CardHeader({
  size,
  className,
  ...props
}: ComponentProps<'div'> &
  VariantProps<typeof cardHeaderVariants>): JSX.Element {
  return (
    <div
      data-slot='card-header'
      className={cn(
        cardHeaderVariants({
          size,
        }),
        className,
      )}
      {...props}
    />
  );
}

export function CardTitle({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot='card-title'
      className={cn('leading-none font-semibold', className)}
      {...props}
    />
  );
}

export function CardDescription({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot='card-description'
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export function CardAction({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot='card-action'
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  );
}

const cardContentVariants = cva('', {
  variants: {
    size: {
      md: 'px-6',
      sm: 'px-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export function CardContent({
  size,
  className,
  ...props
}: ComponentProps<'div'> &
  VariantProps<typeof cardContentVariants>): JSX.Element {
  return (
    <div
      data-slot='card-content'
      className={cn(
        cardContentVariants({
          size,
        }),
        className,
      )}
      {...props}
    />
  );
}

const cardFooterVariants = cva('flex items-center', {
  variants: {
    size: {
      md: 'px-6 [.border-t]:pt-6',
      sm: 'px-4 [.border-t]:pt-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export function CardFooter({
  size,
  className,
  ...props
}: ComponentProps<'div'> &
  VariantProps<typeof cardFooterVariants>): JSX.Element {
  return (
    <div
      data-slot='card-footer'
      className={cn(
        cardFooterVariants({
          size,
        }),
        className,
      )}
      {...props}
    />
  );
}
